import { matchRoutes } from 'react-router-dom';

const ERROR_MESSAGES = {
  OTP_NOT_FOUND: '验证码不存在',
  INVALID_OTP: '验证码错误',
  OTP_EXPIRED: '验证码已过期，请重新获取',
  SMS_SEND_FAILED: '验证码发送失败，请稍后再试',
};

export const LOGIN_CONTEXT = {
  ADMIN: 'admin',
  TENANT: 'tenant',
};

export const LOGIN_CONTEXT_ERROR_MESSAGE = '登录失败，请检查账号或登录入口';

const ADMIN_LOGIN_ROUTES = [{ path: '/admin/login' }];
const ADMIN_ROUTES = [{ path: '/admin/*' }];

const isAdminLoginPath = (pathname) =>
  Boolean(pathname && matchRoutes(ADMIN_LOGIN_ROUTES, pathname));

export const isFeishuSsoLoginType = () => process.env.VITE_LOGIN_TYPE === 'sso-feishu';

export const resolveLoginErrorMessage = (error, fallback = '登录失败，请重试') => {
  if (!error) {
    return fallback;
  }
  return ERROR_MESSAGES[error.code] || error.message || fallback;
};

export const isAdminPath = (pathname) =>
  Boolean(pathname) && !isAdminLoginPath(pathname) && Boolean(matchRoutes(ADMIN_ROUTES, pathname));

export const getLoginPathForTarget = (pathname) => (isAdminPath(pathname) ? '/admin/login' : '/');

const isAllowedRedirectPath = (pathname, loginContext) => {
  if (!pathname) {
    return false;
  }
  if (loginContext === LOGIN_CONTEXT.ADMIN) {
    return isAdminPath(pathname);
  }
  return !isAdminPath(pathname) && !isAdminLoginPath(pathname);
};

export const getSafeRedirectUrl = (redirect, { loginContext = LOGIN_CONTEXT.TENANT } = {}) => {
  if (!redirect?.startsWith('/')) {
    return undefined;
  }
  if (redirect.startsWith('//')) {
    return undefined;
  }
  try {
    const parsed = new URL(redirect, window.location.origin);
    if (parsed.origin !== window.location.origin) {
      return undefined;
    }
    if (!isAllowedRedirectPath(parsed.pathname, loginContext)) {
      return undefined;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return undefined;
  }
};

export const getLoginCallbackUrl = (redirect, { loginContext = LOGIN_CONTEXT.TENANT } = {}) => {
  const safeRedirect = getSafeRedirectUrl(redirect, { loginContext }) || '/';
  return new URL(safeRedirect, window.location.origin).toString();
};

export const getDefaultOrganizationPath = (organizations) => {
  const organizationList = Array.isArray(organizations) ? organizations : [];
  if (organizationList.length === 0) {
    return '/welcome';
  }
  return `/${encodeURIComponent(organizationList[0].id)}`;
};

export const resolveLoginPostPath = ({
  loginContext = LOGIN_CONTEXT.TENANT,
  redirect,
  from,
  organizations,
}) => {
  const safeRedirect = getSafeRedirectUrl(redirect, { loginContext });
  if (safeRedirect) {
    return safeRedirect;
  }

  if (isAllowedRedirectPath(from, loginContext)) {
    return from;
  }

  if (loginContext === LOGIN_CONTEXT.ADMIN) {
    return '/admin';
  }

  return getDefaultOrganizationPath(organizations);
};

export const getOrgRedirectPlan = ({ pathname, organizationId, organizations, from }) => {
  const organizationList = Array.isArray(organizations) ? organizations : [];
  const isLoginPage = pathname === '/';
  const isWelcomePage = pathname === '/welcome';
  const hasOrganizations = organizationList.length > 0;
  const defaultOrganizationPath = getDefaultOrganizationPath(organizationList);

  if (isLoginPage) {
    return { to: from || defaultOrganizationPath };
  }

  if (isWelcomePage) {
    return hasOrganizations ? { to: defaultOrganizationPath } : null;
  }

  if (!hasOrganizations) {
    return { to: '/welcome', message: { type: 'warning', text: '未发现可用组织' } };
  }

  if (!organizationId) {
    return { to: defaultOrganizationPath };
  }

  const isValidOrg = organizationList.some((org) => org.id === organizationId);
  if (!isValidOrg) {
    return {
      to: defaultOrganizationPath,
      message: { type: 'error', text: '您没有权限访问该组织' },
    };
  }

  return null;
};
